import React, { useState, useCallback, useRef } from 'react';
import BlockManager, { UnifiedBlock } from '../../services/blockManager';
import { habitService, Habit } from '../../services/habitService';
import { NotesService, Note } from '../../services/notesService';
import { useAuth } from '../../contexts/AuthContext';
import { uploadImage, uploadImageFromBlob } from '../../services/imageUploadService';
import { translateText } from '../../services/translationService';
import BlockItem from './BlockItem';
import NoteLinkSelector from './NoteLinkSelector';
import ImageLightbox from './ImageLightbox';
import TranslationDialog from './TranslationDialog';
import './BlockEditor.css';

interface TriggerInfo {
  type: 'command' | 'markdown-link';
  blockId: string;
  position: { top: number; left: number };
  context?: {
    searchQuery?: string;
    startPosition?: number;
  };
}

interface BlockEditorProps {
  blockManager: BlockManager;
  blocks?: UnifiedBlock[]; // Optional blocks prop to override internal state
  placeholder?: string;
  onSyncStatusChange?: (status: 'synced' | 'syncing' | 'error') => void;
  fullBlockMode?: boolean;
  onToggleFullBlockMode?: () => void;
}

interface Command {
  id: string;
  label: string;
  description: string;
  icon: string;
}

interface CommandPaletteProps {
  isVisible: boolean;
  commands: Command[];
  selectedIndex: number;
  onSelect: (command: Command) => void;
  onClose: () => void;
  position: { top: number; left: number };
}

interface HabitSelectorProps {
  isVisible: boolean;
  habits: Habit[];
  selectedIndex: number;
  onSelect: (habit: Habit) => void;
  onClose: () => void;
  position: { top: number; left: number };
  loading?: boolean;
}

interface NoteSelectorProps {
  isVisible: boolean;
  notes: Note[];
  selectedIndex: number;
  onSelect: (note: Note) => void;
  onClose: () => void;
  position: { top: number; left: number };
  loading?: boolean;
}

// CommandPalette component
const CommandPalette: React.FC<CommandPaletteProps> = ({
  isVisible,
  commands,
  selectedIndex,
  onSelect,
  onClose,
  position
}) => {
  if (!isVisible) return null;

  return (
    <div 
      className="command-palette"
      style={{ top: position.top, left: position.left }}
    >
      <div className="command-palette-content">
        {commands.map((command, index) => (
          <div
            key={command.id}
            className={`command-item ${index === selectedIndex ? 'selected' : ''}`}
            onClick={() => onSelect(command)}
          >
            <span className="command-icon">{command.icon}</span>
            <div className="command-text">
              <span className="command-label">{command.label}</span>
              <span className="command-description">{command.description}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// HabitSelector component
const HabitSelector: React.FC<HabitSelectorProps> = ({
  isVisible,
  habits,
  selectedIndex,
  onSelect,
  onClose,
  position,
  loading = false
}) => {
  if (!isVisible) return null;

  return (
    <div 
      className="habit-selector"
      style={{ top: position.top, left: position.left }}
    >
      <div className="habit-selector-content">
        <div className="habit-selector-header">
          <span>Select a habit</span>
        </div>
        {loading ? (
          <div className="habit-selector-loading">Loading habits...</div>
        ) : (
          <div className="habit-list">
            {habits.length === 0 ? (
              <div className="habit-selector-empty">No habits found</div>
            ) : (
              habits.map((habit, index) => (
                <div
                  key={habit.id}
                  className={`habit-item ${index === selectedIndex ? 'selected' : ''}`}
                  onClick={() => onSelect(habit)}
                >
                  <span className="habit-icon">{habit.icon || '📋'}</span>
                  <div className="habit-text">
                    <span className="habit-name">{habit.name}</span>
                    {habit.category && (
                      <span className="habit-category">{habit.category}</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// NoteSelector component
const NoteSelector: React.FC<NoteSelectorProps> = ({
  isVisible,
  notes,
  selectedIndex,
  onSelect,
  onClose,
  position,
  loading = false
}) => {
  if (!isVisible) return null;

  return (
    <div 
      className="note-selector"
      style={{ top: position.top, left: position.left }}
    >
      <div className="note-selector-content">
        <div className="note-selector-header">
          <span>Select a note</span>
        </div>
        {loading ? (
          <div className="note-selector-loading">Loading notes...</div>
        ) : (
          <div className="note-list">
            {notes.length === 0 ? (
              <div className="note-selector-empty">No notes found</div>
            ) : (
              notes.map((note, index) => (
                <div
                  key={note.note_id}
                  className={`note-item ${index === selectedIndex ? 'selected' : ''}`}
                  onClick={() => onSelect(note)}
                >
                  <span className="note-icon">📝</span>
                  <div className="note-text">
                    <span className="note-title">{note.title}</span>
                    <span className="note-id">/{note.note_id}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Main BlockEditor component
const BlockEditor: React.FC<BlockEditorProps> = ({
  blockManager,
  blocks: propBlocks,
  placeholder = "Start typing...",
  onSyncStatusChange,
  fullBlockMode = false,
  onToggleFullBlockMode
}) => {
  const { user } = useAuth();
  const [focusedBlockId, setFocusedBlockId] = useState<string | null>(null);
  const [internalBlocks, setInternalBlocks] = useState<UnifiedBlock[]>([]);
  const initialBlockCreatedRef = React.useRef(false);
  
  // Command palette state
  const [commandPaletteState, setCommandPaletteState] = useState<{
    isVisible: boolean;
    blockId: string | null;
    position: { top: number; left: number };
    selectedIndex: number;
  }>({
    isVisible: false,
    blockId: null,
    position: { top: 0, left: 0 },
    selectedIndex: 0
  });

  // Habit selector state
  const [habitSelectorState, setHabitSelectorState] = useState<{
    isVisible: boolean;
    blockId: string | null;
    position: { top: number; left: number };
    selectedIndex: number;
    habits: Habit[];
    loading: boolean;
  }>({
    isVisible: false,
    blockId: null,
    position: { top: 0, left: 0 },
    selectedIndex: 0,
    habits: [],
    loading: false
  });

  // Note selector state
  const [noteSelectorState, setNoteSelectorState] = useState<{
    isVisible: boolean;
    blockId: string | null;
    position: { top: number; left: number };
    selectedIndex: number;
    notes: Note[];
    loading: boolean;
  }>({
    isVisible: false,
    blockId: null,
    position: { top: 0, left: 0 },
    selectedIndex: 0,
    notes: [],
    loading: false
  });

  // Markdown link selector state
  const [markdownLinkState, setMarkdownLinkState] = useState<{
    isVisible: boolean;
    blockId: string | null;
    position: { top: number; left: number };
    selectedIndex: number;
    searchQuery: string;
    startPosition: number;
    notes: Note[];
    loading: boolean;
  }>({
    isVisible: false,
    blockId: null,
    position: { top: 0, left: 0 },
    selectedIndex: 0,
    searchQuery: '',
    startPosition: 0,
    notes: [],
    loading: false
  });

  // Note caching system
  const [cachedNotes, setCachedNotes] = useState<Note[]>([]);
  const [notesLastFetched, setNotesLastFetched] = useState<number>(0);
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // Image lightbox state
  const [lightboxState, setLightboxState] = useState<{
    isVisible: boolean;
    imageUrl: string;
    metadata?: { originalName?: string; width?: number; height?: number };
  }>({
    isVisible: false,
    imageUrl: ''
  });

  // Translation dialog state
  const [translationDialogState, setTranslationDialogState] = useState<{
    isVisible: boolean;
    blockId: string | null;
    sourceText: string;
    isTranslating: boolean;
    error: string | null;
  }>({
    isVisible: false,
    blockId: null,
    sourceText: '',
    isTranslating: false,
    error: null
  });

  // Available commands
  const availableCommands: Command[] = [
    {
      id: 'habit',
      label: '/habit',
      description: 'Link to a habit',
      icon: '📋'
    },
    {
      id: 'note',
      label: '/note',
      description: 'Link to a note',
      icon: '📝'
    }
  ];

  // Load user notes for caching
  const loadUserNotes = useCallback(async () => {
    if (!user?.id) return;
    
    const now = Date.now();
    if (cachedNotes.length > 0 && (now - notesLastFetched) < CACHE_DURATION) {
      return; // Use cached notes
    }
    
    try {
      const response = await NotesService.getUserNotes(user.id, { 
        limit: 500, 
        sort_order: 'DESC' 
      });
      if (response.success && response.data) {
        setCachedNotes(response.data);
        setNotesLastFetched(now);
      }
    } catch (error) {
      console.error('Error loading notes for cache:', error);
    }
  }, [user?.id, cachedNotes.length, notesLastFetched, CACHE_DURATION]);

  // Filter notes for search
  const filterNotes = useCallback((notes: Note[], query: string): Note[] => {
    if (!query.trim()) return notes;
    
    const searchTerm = query.toLowerCase();
    return notes.filter(note => 
      note.title.toLowerCase().includes(searchTerm) ||
      note.note_id.toLowerCase().includes(searchTerm)
    ).sort((a, b) => {
      // Prioritize exact matches and title matches
      const aTitle = a.title.toLowerCase();
      const bTitle = b.title.toLowerCase();
      
      if (aTitle.startsWith(searchTerm) && !bTitle.startsWith(searchTerm)) return -1;
      if (!aTitle.startsWith(searchTerm) && bTitle.startsWith(searchTerm)) return 1;
      
      return a.title.localeCompare(b.title);
    });
  }, []);

  // Load notes on component mount and when user changes
  React.useEffect(() => {
    loadUserNotes();
  }, [loadUserNotes]);

  // Command trigger handler
  const handleCommandTrigger = useCallback((blockId: string, position: { top: number; left: number }) => {
    setCommandPaletteState({
      isVisible: true,
      blockId,
      position,
      selectedIndex: 0
    });
  }, []);

  // Enhanced trigger handler for both commands and markdown links
  const handleTrigger = useCallback((triggerInfo: TriggerInfo) => {
    if (triggerInfo.type === 'command') {
      // Existing command palette logic
      setCommandPaletteState({
        isVisible: true,
        blockId: triggerInfo.blockId,
        position: triggerInfo.position,
        selectedIndex: 0
      });
    } else if (triggerInfo.type === 'markdown-link') {
      // New markdown link logic
      setMarkdownLinkState({
        isVisible: true,
        blockId: triggerInfo.blockId,
        position: triggerInfo.position,
        selectedIndex: 0,
        searchQuery: triggerInfo.context?.searchQuery || '',
        startPosition: triggerInfo.context?.startPosition || 0,
        notes: cachedNotes, // Use cached notes for immediate display
        loading: false
      });
    }
  }, [cachedNotes]);

  // Command selection handler
  const handleCommandSelect = useCallback(async (command: Command) => {
    if (!commandPaletteState.blockId) return;

    // Remove the '/' character from the block content before proceeding
    const currentBlock = blockManager.getBlockById(commandPaletteState.blockId);
    if (currentBlock) {
      const content = currentBlock.content || '';
      // Find and remove the '/' at the beginning
      const cleanContent = content.replace(/^\/\s*/, '');
      await blockManager.updateBlockContent(commandPaletteState.blockId, cleanContent);
      
      // Update internal state if using internal state management
      if (!propBlocks) {
        setInternalBlocks(blockManager.getContentBlocks());
      }
    }

    setCommandPaletteState({
      isVisible: false,
      blockId: null,
      position: { top: 0, left: 0 },
      selectedIndex: 0
    });

    if (command.id === 'habit') {
      // Load habits and show habit selector
      if (!user?.id) return;

      setHabitSelectorState(prev => ({
        ...prev,
        isVisible: true,
        blockId: commandPaletteState.blockId,
        position: commandPaletteState.position,
        selectedIndex: 0,
        loading: true
      }));

      try {
        const response = await habitService.getUserHabits(user.id, { is_active: true });
        if (response.success && response.habits) {
          setHabitSelectorState(prev => ({
            ...prev,
            habits: response.habits || [],
            loading: false
          }));
        }
      } catch (error) {
        console.error('Error loading habits:', error);
        setHabitSelectorState(prev => ({
          ...prev,
          loading: false,
          habits: []
        }));
      }
    } else if (command.id === 'note') {
      // Load notes and show note selector
      if (!user?.id) return;

      setNoteSelectorState(prev => ({
        ...prev,
        isVisible: true,
        blockId: commandPaletteState.blockId,
        position: {left: 20, top: 50 },
        selectedIndex: 0,
        loading: true
      }));

      try {
        const response = await NotesService.getUserNotes(user.id, { limit: 50, sort_order: 'DESC' });
        if (response.success && response.data) {
          setNoteSelectorState(prev => ({
            ...prev,
            notes: response.data || [],
            loading: false
          }));
        }
      } catch (error) {
        console.error('Error loading notes:', error);
        setNoteSelectorState(prev => ({
          ...prev,
          loading: false,
          notes: []
        }));
      }
    }
  }, [commandPaletteState.blockId, commandPaletteState.position, user?.id, blockManager, propBlocks]);

  // Habit selection handler
  const handleHabitSelect = useCallback(async (habit: Habit) => {
    if (!habitSelectorState.blockId) return;

    // Close habit selector
    setHabitSelectorState({
      isVisible: false,
      blockId: null,
      position: { top: 0, left: 0 },
      selectedIndex: 0,
      habits: [],
      loading: false
    });

    // Convert block to habit link
    const habitData = {
      id: habit.id,
      name: habit.name,
      icon: habit.icon,
      category: habit.category
    };

    // Update block type and content in a single API call
    await blockManager.updateBlockTypeAndContent(habitSelectorState.blockId, 'habit-link', JSON.stringify(habitData));
    
    // Refresh blocks if using internal state
    if (!propBlocks) {
      setInternalBlocks(blockManager.getContentBlocks());
    }
  }, [habitSelectorState.blockId, blockManager, propBlocks]);

  // Note selection handler
  const handleNoteSelect = useCallback(async (note: Note) => {
    if (!noteSelectorState.blockId) return;

    // Close note selector
    setNoteSelectorState({
      isVisible: false,
      blockId: null,
      position: { top: 0, left: 0 },
      selectedIndex: 0,
      notes: [],
      loading: false
    });

    // Convert block to note link
    const noteData = {
      note_id: note.note_id,
      title: note.title
    };

    // Update block type and content in a single API call
    await blockManager.updateBlockTypeAndContent(noteSelectorState.blockId, 'note-link', JSON.stringify(noteData));
    
    // Refresh blocks if using internal state
    if (!propBlocks) {
      setInternalBlocks(blockManager.getContentBlocks());
    }
  }, [noteSelectorState.blockId, blockManager, propBlocks]);

  // Markdown link selection handler
  const handleMarkdownLinkSelect = useCallback(async (note: Note) => {
    if (!markdownLinkState.blockId) return;

    const currentBlock = blockManager.getBlockById(markdownLinkState.blockId);
    if (!currentBlock) return;

    // Replace [[search-text]] with link
    const content = currentBlock.content || '';
    const beforeLink = content.substring(0, markdownLinkState.startPosition);
    const afterSearch = content.substring(markdownLinkState.startPosition + 2); // Skip [[
    const searchEndIndex = afterSearch.indexOf(']]');
    const afterLink = searchEndIndex >= 0 ? afterSearch.substring(searchEndIndex + 2) : afterSearch;

    const newContent = beforeLink + `[[${note.title}]]` + afterLink;

    // Create link metadata
    const linkData = {
      noteId: note.note_id,
      noteTitle: note.title,
      linkText: note.title
    };

    // Update block content
    await blockManager.updateBlockContent(markdownLinkState.blockId, newContent);
    
    // Update block properties with markdown link metadata
    const currentProperties = currentBlock.properties || {};
    const markdownLinks = currentProperties.markdownLinks || {};
    markdownLinks[note.title] = linkData;
    
    await blockManager.updateBlockProperties(markdownLinkState.blockId, {
      ...currentProperties,
      markdownLinks
    });

    // Close selector
    setMarkdownLinkState({
      isVisible: false,
      blockId: null,
      position: { top: 0, left: 0 },
      selectedIndex: 0,
      searchQuery: '',
      startPosition: 0,
      notes: [],
      loading: false
    });

    // Refresh blocks if using internal state
    if (!propBlocks) {
      setInternalBlocks(blockManager.getContentBlocks());
    }
  }, [markdownLinkState, blockManager, propBlocks]);

  // Handle creating new note from markdown link
  const handleMarkdownLinkCreateNew = useCallback(async (title: string) => {
    if (!markdownLinkState.blockId || !user?.id) return;

    try {
      // Create new note with the specified title
      const newNote = await NotesService.createNoteFromTitle(user.id, title, []);

      // Now handle it as a regular note selection
      await handleMarkdownLinkSelect(newNote);

      // Update cached notes to include the new note
      setCachedNotes(prev => [newNote, ...prev]);
    } catch (error) {
      console.error('Error creating new note:', error);
      // Could show an error message to user here
    }
  }, [markdownLinkState.blockId, user?.id, handleMarkdownLinkSelect]);

  // Close command palette handler
  const handleCloseCommandPalette = useCallback(() => {
    setCommandPaletteState({
      isVisible: false,
      blockId: null,
      position: { top: 0, left: 0 },
      selectedIndex: 0
    });
  }, []);

  // Close habit selector handler
  const handleCloseHabitSelector = useCallback(() => {
    setHabitSelectorState({
      isVisible: false,
      blockId: null,
      position: { top: 0, left: 0 },
      selectedIndex: 0,
      habits: [],
      loading: false
    });
  }, []);

  // Close note selector handler
  const handleCloseNoteSelector = useCallback(() => {
    setNoteSelectorState({
      isVisible: false,
      blockId: null,
      position: { top: 0, left: 0 },
      selectedIndex: 0,
      notes: [],
      loading: false
    });
  }, []);

  // Close markdown link selector handler
  const handleCloseMarkdownLinkSelector = useCallback(() => {
    setMarkdownLinkState({
      isVisible: false,
      blockId: null,
      position: { top: 0, left: 0 },
      selectedIndex: 0,
      searchQuery: '',
      startPosition: 0,
      notes: [],
      loading: false
    });
  }, []);

  // Handle markdown link search change
  const handleMarkdownLinkSearchChange = useCallback((query: string) => {
    setMarkdownLinkState(prev => ({
      ...prev,
      searchQuery: query,
      selectedIndex: 0 // Reset selection when search changes
    }));
  }, []);

  // Handle image upload and create image block
  const handleImageUpload = useCallback(async (file: File, afterBlockId?: string) => {
    if (!user?.id) return;

    try {
      // Upload image
      const { imageUrl, metadata } = await uploadImage(file);

      // Create image block with image URL as content
      const newBlockId = await blockManager.createBlock(imageUrl, afterBlockId);
      
      if (newBlockId) {
        // Update block type to image and set properties with metadata
        await blockManager.updateBlockTypeAndContent(newBlockId, 'image', imageUrl, undefined);
        
        // Store metadata in properties
        await blockManager.updateBlockProperties(newBlockId, {
          imageMetadata: {
            originalName: metadata.originalName,
            width: metadata.width,
            height: metadata.height,
            mimeType: metadata.mimeType
          }
        });
        
        // Update internal state if using internal state management
        if (!propBlocks) {
          setInternalBlocks(blockManager.getContentBlocks());
        }
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      // Could show error message to user here
    }
  }, [user?.id, blockManager, propBlocks]);

  // Handle drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const files = Array.from(e.dataTransfer.files).filter(file => file.type.startsWith('image/'));
    if (files.length === 0) return;

    // Find the block to insert after (if dropping on a specific block)
    const target = e.target as HTMLElement;
    const blockElement = target.closest('[data-block-id]');
    const afterBlockId = blockElement ? blockElement.getAttribute('data-block-id') || undefined : undefined;

    // Upload each image
    for (const file of files) {
      await handleImageUpload(file, afterBlockId);
    }
  }, [handleImageUpload]);

  // Handle paste from clipboard
  const handlePaste = useCallback(async (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items);
    const imageItem = items.find(item => item.type.startsWith('image/'));

    if (imageItem) {
      e.preventDefault();
      const blob = imageItem.getAsFile();
      if (blob) {
        try {
          const { imageUrl, metadata } = await uploadImageFromBlob(blob);
          
          // Get current blocks for finding target block
          const currentBlocks = propBlocks || internalBlocks || [];
          const targetBlockId = focusedBlockId || (currentBlocks.length > 0 ? currentBlocks[currentBlocks.length - 1].block_id : undefined);

          const newBlockId = await blockManager.createBlock(imageUrl, targetBlockId);
          
          if (newBlockId) {
            await blockManager.updateBlockTypeAndContent(newBlockId, 'image', imageUrl, undefined);
            
            // Store metadata in properties
            await blockManager.updateBlockProperties(newBlockId, {
              imageMetadata: {
                originalName: metadata.originalName,
                width: metadata.width,
                height: metadata.height,
                mimeType: metadata.mimeType
              }
            });
            
            if (!propBlocks) {
              setInternalBlocks(blockManager.getContentBlocks());
            }
          }
        } catch (error) {
          console.error('Error pasting image:', error);
        }
      }
    }
  }, [focusedBlockId, propBlocks, internalBlocks, blockManager]);

  // Handle opening lightbox
  const handleOpenLightbox = useCallback((imageUrl: string, metadata?: { originalName?: string; width?: number; height?: number }) => {
    setLightboxState({
      isVisible: true,
      imageUrl,
      metadata
    });
  }, []);

  const handleCloseLightbox = useCallback(() => {
    setLightboxState({
      isVisible: false,
      imageUrl: ''
    });
  }, []);

  // Keyboard navigation for command palette and selectors
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (commandPaletteState.isVisible) {
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            setCommandPaletteState(prev => ({
              ...prev,
              selectedIndex: Math.min(prev.selectedIndex + 1, availableCommands.length - 1)
            }));
            break;
          case 'ArrowUp':
            e.preventDefault();
            setCommandPaletteState(prev => ({
              ...prev,
              selectedIndex: Math.max(prev.selectedIndex - 1, 0)
            }));
            break;
          case 'Enter':
            e.preventDefault();
            const selectedCommand = availableCommands[commandPaletteState.selectedIndex];
            if (selectedCommand) {
              handleCommandSelect(selectedCommand);
            }
            break;
          case 'Escape':
            e.preventDefault();
            handleCloseCommandPalette();
            break;
        }
      } else if (habitSelectorState.isVisible) {
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            setHabitSelectorState(prev => ({
              ...prev,
              selectedIndex: Math.min(prev.selectedIndex + 1, prev.habits.length - 1)
            }));
            break;
          case 'ArrowUp':
            e.preventDefault();
            setHabitSelectorState(prev => ({
              ...prev,
              selectedIndex: Math.max(prev.selectedIndex - 1, 0)
            }));
            break;
          case 'Enter':
            e.preventDefault();
            const selectedHabit = habitSelectorState.habits[habitSelectorState.selectedIndex];
            if (selectedHabit) {
              handleHabitSelect(selectedHabit);
            }
            break;
          case 'Escape':
            e.preventDefault();
            handleCloseHabitSelector();
            break;
        }
        } else if (noteSelectorState.isVisible) {
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            setNoteSelectorState(prev => ({
              ...prev,
              selectedIndex: Math.min(prev.selectedIndex + 1, prev.notes.length - 1)
            }));
            break;
          case 'ArrowUp':
            e.preventDefault();
            setNoteSelectorState(prev => ({
              ...prev,
              selectedIndex: Math.max(prev.selectedIndex - 1, 0)
            }));
            break;
          case 'Enter':
            e.preventDefault();
            const selectedNote = noteSelectorState.notes[noteSelectorState.selectedIndex];
            if (selectedNote) {
              handleNoteSelect(selectedNote);
            }
            break;
          case 'Escape':
            e.preventDefault();
            handleCloseNoteSelector();
            break;
        }
      } else if (markdownLinkState.isVisible) {
        const filteredNotes = filterNotes(markdownLinkState.notes, markdownLinkState.searchQuery);
        // Check if we should show create option
        const shouldShowCreateOption = markdownLinkState.searchQuery.trim() && 
          !filteredNotes.some(note => note.title.toLowerCase() === markdownLinkState.searchQuery.toLowerCase());
        const totalItems = filteredNotes.length + (shouldShowCreateOption ? 1 : 0);
        
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            setMarkdownLinkState(prev => ({
              ...prev,
              selectedIndex: Math.min(prev.selectedIndex + 1, totalItems - 1)
            }));
            break;
          case 'ArrowUp':
            e.preventDefault();
            setMarkdownLinkState(prev => ({
              ...prev,
              selectedIndex: Math.max(prev.selectedIndex - 1, 0)
            }));
            break;
          case 'Enter':
            e.preventDefault();
            if (markdownLinkState.selectedIndex < filteredNotes.length) {
              // Select existing note
              const selectedMarkdownNote = filteredNotes[markdownLinkState.selectedIndex];
              if (selectedMarkdownNote) {
                handleMarkdownLinkSelect(selectedMarkdownNote);
              }
            } else if (shouldShowCreateOption && markdownLinkState.selectedIndex === filteredNotes.length) {
              // Create new note
              handleMarkdownLinkCreateNew(markdownLinkState.searchQuery.trim());
            }
            break;
          case 'Escape':
            e.preventDefault();
            handleCloseMarkdownLinkSelector();
            break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    commandPaletteState.isVisible, 
    commandPaletteState.selectedIndex, 
    habitSelectorState.isVisible, 
    habitSelectorState.selectedIndex,
    habitSelectorState.habits,
    noteSelectorState.isVisible,
    noteSelectorState.selectedIndex,
    noteSelectorState.notes,
    markdownLinkState.isVisible,
    markdownLinkState.selectedIndex,
    markdownLinkState.notes,
    markdownLinkState.searchQuery,
    availableCommands,
    handleCommandSelect,
    handleHabitSelect,
    handleNoteSelect,
    handleMarkdownLinkSelect,
    handleMarkdownLinkCreateNew,
    handleCloseCommandPalette,
    handleCloseHabitSelector,
    handleCloseNoteSelector,
    handleCloseMarkdownLinkSelector,
    filterNotes
  ]);

  // Click outside to close palettes
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      if (commandPaletteState.isVisible) {
        if (!target.closest('.command-palette') && !target.closest('[contenteditable="true"]')) {
          handleCloseCommandPalette();
        }
      }
      
      if (habitSelectorState.isVisible) {
        if (!target.closest('.habit-selector') && !target.closest('[contenteditable="true"]')) {
          handleCloseHabitSelector();
        }
      }

      if (noteSelectorState.isVisible) {
        if (!target.closest('.note-selector') && !target.closest('[contenteditable="true"]')) {
          handleCloseNoteSelector();
        }
      }

      if (markdownLinkState.isVisible) {
        if (!target.closest('.note-link-selector') && !target.closest('[contenteditable="true"]')) {
          handleCloseMarkdownLinkSelector();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [
    commandPaletteState.isVisible, 
    habitSelectorState.isVisible, 
    noteSelectorState.isVisible,
    markdownLinkState.isVisible,
    handleCloseCommandPalette, 
    handleCloseHabitSelector,
    handleCloseNoteSelector,
    handleCloseMarkdownLinkSelector
  ]);

  // Use prop blocks if provided, otherwise use internal state
  const blocks = propBlocks || internalBlocks || [];

  // Initialize internal blocks if no prop blocks provided
  React.useEffect(() => {
    // Only manage internal state if no prop blocks are provided
    if (!propBlocks) {
      setInternalBlocks(blockManager.getContentBlocks());
    }
  }, [blockManager, propBlocks]);

  const handleFocus = useCallback((blockId: string, cursorPosition?: number) => {
    setFocusedBlockId(blockId);
    
    // Focus the DOM element
    setTimeout(() => {
      const element = document.querySelector(`[data-block-id="${blockId}"]`);
      if (element) {
        const contenteditable = element.querySelector('[contenteditable="true"]') as HTMLElement;
        if (contenteditable) {
          contenteditable.focus();
          
          // Set cursor position if specified
          if (cursorPosition !== undefined) {
            const range = document.createRange();
            const selection = window.getSelection();
            
            try {
              const textContent = contenteditable.textContent || '';
              const position = Math.min(cursorPosition, textContent.length);
              
              if (textContent.length === 0) {
                range.setStart(contenteditable, 0);
                range.setEnd(contenteditable, 0);
              } else {
                const textNode = contenteditable.firstChild;
                if (textNode && textNode.nodeType === Node.TEXT_NODE) {
                  range.setStart(textNode, position);
                  range.setEnd(textNode, position);
                } else {
                  range.setStart(contenteditable, 0);
                  range.setEnd(contenteditable, 0);
                }
              }
              
              selection?.removeAllRanges();
              selection?.addRange(range);
            } catch (error) {
              console.error('Error setting cursor position:', error);
            }
          }
        }
      }
    }, 10);
  }, []);

  // Handler functions for block operations
  const handleUpdateContent = useCallback(async (blockId: string, content: string) => {
    await blockManager.updateBlockContent(blockId, content);
  }, [blockManager]);

  const handleSplitBlock = useCallback(async (blockId: string, cursorPosition: number) => {
    const newBlockId = await blockManager.splitBlock(blockId, cursorPosition);
    if (newBlockId) {
      handleFocus(newBlockId, 0);
    }
  }, [blockManager, handleFocus]);

  const handleMergeBlock = useCallback(async (currentBlockId: string, _: string) => {
    const navInfo = blockManager.getNavigationInfo(currentBlockId);
    if (navInfo.previousBlockId) {
      // Get the previous block content before merging to calculate cursor position
      const previousBlock = blockManager.getBlockById(navInfo.previousBlockId);
      const previousContentLength = previousBlock?.content?.length || 0;
      
      const success = await blockManager.mergeBlocks(currentBlockId, navInfo.previousBlockId);
      if (success) {
        // Focus at the end of the previous block's original content
        handleFocus(navInfo.previousBlockId, previousContentLength);
      }
    }
  }, [blockManager, handleFocus]);

  const handleNavigateUp = useCallback((currentBlockId: string) => {
    const navInfo = blockManager.getNavigationInfo(currentBlockId);
    if (navInfo.previousBlockId) {
      handleFocus(navInfo.previousBlockId);
    }
  }, [blockManager, handleFocus]);

  const handleNavigateDown = useCallback((currentBlockId: string) => {
    const navInfo = blockManager.getNavigationInfo(currentBlockId);
    if (navInfo.nextBlockId) {
      handleFocus(navInfo.nextBlockId);
    }
  }, [blockManager, handleFocus]);

  const handleInsertBlockBelow = useCallback(async (blockId: string) => {
    const newBlockId = await blockManager.createBlock('', blockId);
    if (newBlockId) {
      handleFocus(newBlockId, 0);
      // Update internal state if using internal state management
      if (!propBlocks) {
        setInternalBlocks(blockManager.getContentBlocks());
      }
    }
  }, [blockManager, handleFocus, propBlocks]);

  const handleDeleteBlock = useCallback(async (blockId: string) => {
    await blockManager.deleteBlock(blockId);
    // Update internal state if using internal state management
    if (!propBlocks) {
      setInternalBlocks(blockManager.getContentBlocks());
    }
  }, [blockManager, propBlocks]);

  // Handle translate block - opens the translation dialog
  const handleTranslateBlock = useCallback((blockId: string) => {
    const block = blockManager.getBlockById(blockId);
    if (!block) return;

    // Don't allow translation of image blocks
    if (block.type === 'image') {
      return;
    }

    const sourceText = block.content || '';
    setTranslationDialogState({
      isVisible: true,
      blockId,
      sourceText,
      isTranslating: false,
      error: null
    });
  }, [blockManager]);

  // Handle translation execution
  const handleTranslationExecute = useCallback(async (targetLanguage: string) => {
    if (!translationDialogState.blockId) return;

    const block = blockManager.getBlockById(translationDialogState.blockId);
    if (!block) {
      setTranslationDialogState(prev => ({
        ...prev,
        error: 'Block not found',
        isTranslating: false
      }));
      return;
    }

    const sourceText = block.content || '';
    if (!sourceText.trim()) {
      setTranslationDialogState(prev => ({
        ...prev,
        error: 'Cannot translate empty block',
        isTranslating: false
      }));
      return;
    }

    // Set translating state
    setTranslationDialogState(prev => ({
      ...prev,
      isTranslating: true,
      error: null
    }));

    try {
      // Call translation service
      const result = await translateText(sourceText, targetLanguage);

      if (result.success && result.translatedText) {
        // Create new block below with translated content
        const newBlockId = await blockManager.createBlock(result.translatedText, translationDialogState.blockId);
        
        if (newBlockId) {
          // Update internal state if using internal state management
          if (!propBlocks) {
            setInternalBlocks(blockManager.getContentBlocks());
          }

          // Close dialog and focus the new block
          setTranslationDialogState({
            isVisible: false,
            blockId: null,
            sourceText: '',
            isTranslating: false,
            error: null
          });

          // Focus the new translated block
          handleFocus(newBlockId, 0);
        } else {
          setTranslationDialogState(prev => ({
            ...prev,
            error: 'Failed to create translated block',
            isTranslating: false
          }));
        }
      } else {
        setTranslationDialogState(prev => ({
          ...prev,
          error: result.error || 'Translation failed',
          isTranslating: false
        }));
      }
    } catch (error: any) {
      console.error('Translation error:', error);
      setTranslationDialogState(prev => ({
        ...prev,
        error: error.message || 'An unexpected error occurred during translation',
        isTranslating: false
      }));
    }
  }, [translationDialogState.blockId, blockManager, propBlocks, handleFocus]);

  // Handle translation dialog close
  const handleTranslationDialogClose = useCallback(() => {
    if (!translationDialogState.isTranslating) {
      setTranslationDialogState({
        isVisible: false,
        blockId: null,
        sourceText: '',
        isTranslating: false,
        error: null
      });
    }
  }, [translationDialogState.isTranslating]);

  const handleBlankAreaClick = useCallback(async (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      if (blocks.length > 0) {
        const lastBlock = blocks[blocks.length - 1];
        handleFocus(lastBlock.block_id);
      } else {
        // Create a new block when clicking in empty editor
        const newBlockId = await blockManager.createInitialBlock();
        if (newBlockId) {
          handleFocus(newBlockId, 0);
          // Update internal state after creating block (only if using internal state)
          if (!propBlocks) {
            setInternalBlocks(blockManager.getContentBlocks());
          }
        }
      }
    }
  }, [blocks, handleFocus, blockManager, propBlocks]);

  // Create initial empty block if none exist
  React.useEffect(() => {
    // Only create initial block if:
    // 1. No blocks exist
    // 2. We're not already creating one
    // 3. We have a valid container ID (for managed blocks)
    // 4. Not using prop blocks (only for internal state management)
    
    if (blocks.length === 0 && !initialBlockCreatedRef.current && !propBlocks) {
      initialBlockCreatedRef.current = true;
      
      // Create an initial empty block for user to start typing
      const createInitialBlock = async () => {
        try {
          const newBlockId = await blockManager.createInitialBlock();
          if (newBlockId) {
            handleFocus(newBlockId, 0);
            setInternalBlocks(blockManager.getContentBlocks());
          }
        } catch (error) {
          console.error('Failed to create initial block:', error);
          // Reset flag on error so it can be retried
          initialBlockCreatedRef.current = false;
        }
      };
      
      // Use requestAnimationFrame instead of setTimeout for better timing
      requestAnimationFrame(() => {
        createInitialBlock();
      });
    }
    
    // Reset the flag if blocks exist (for when switching between different containers)
    if (blocks.length > 0) {
      initialBlockCreatedRef.current = false;
    }
  }, [blocks?.length || 0, blockManager, handleFocus, propBlocks]);

  if (blocks.length === 0) {
    return (
      <div className="block-editor">
        <div className="block-editor-empty" onClick={handleBlankAreaClick}>
          <p>{placeholder}</p>
        </div>
      </div>
    );
  }

  // Debug: Log block IDs to catch duplicates
  const blockIds = blocks.map(b => b.block_id);
  const uniqueIds = [...new Set(blockIds)];
  if (blockIds.length !== uniqueIds.length) {
    console.error('🚨 Duplicate block IDs detected:', {
      totalBlocks: blockIds.length,
      uniqueBlocks: uniqueIds.length,
      blockIds,
      duplicates: blockIds.filter((id, index) => blockIds.indexOf(id) !== index)
    });
  }

  return (
    <div 
      className={`block-editor ${fullBlockMode ? 'full-block-mode' : ''}`}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onPaste={handlePaste}
    >
      <div className="block-list" onClick={handleBlankAreaClick}>
        {blocks.map((block, index) => (
          <BlockItem
            key={block.block_id}
            block={block}
            index={index}
            isLast={index === blocks.length - 1}
            isFocused={focusedBlockId === block.block_id}
            onFocus={handleFocus}
            onUpdateContent={handleUpdateContent}
            onSplitBlock={handleSplitBlock}
            onMergeBlock={handleMergeBlock}
            onNavigateUp={handleNavigateUp}
            onNavigateDown={handleNavigateDown}
            onCommandTrigger={handleCommandTrigger}
            onTrigger={handleTrigger}
            fullBlockMode={fullBlockMode}
            onInsertBlockBelow={handleInsertBlockBelow}
            onDeleteBlock={handleDeleteBlock}
            onImageClick={handleOpenLightbox}
            onTranslateBlock={handleTranslateBlock}
          />
        ))}
      </div>

      {/* Command Palette */}
      <CommandPalette
        isVisible={commandPaletteState.isVisible}
        commands={availableCommands}
        selectedIndex={commandPaletteState.selectedIndex}
        onSelect={handleCommandSelect}
        onClose={handleCloseCommandPalette}
        position={commandPaletteState.position}
      />
      
      {/* Habit Selector */}
      <HabitSelector
        isVisible={habitSelectorState.isVisible}
        habits={habitSelectorState.habits}
        selectedIndex={habitSelectorState.selectedIndex}
        onSelect={handleHabitSelect}
        onClose={handleCloseHabitSelector}
        position={habitSelectorState.position}
        loading={habitSelectorState.loading}
      />
      
      {/* Note Selector */}
      <NoteSelector
        isVisible={noteSelectorState.isVisible}
        notes={noteSelectorState.notes}
        selectedIndex={noteSelectorState.selectedIndex}
        onSelect={handleNoteSelect}
        onClose={handleCloseNoteSelector}
        position={noteSelectorState.position}
        loading={noteSelectorState.loading}
      />
      
      {/* Markdown Link Selector */}
      <NoteLinkSelector
        isVisible={markdownLinkState.isVisible}
        notes={filterNotes(markdownLinkState.notes, markdownLinkState.searchQuery)}
        searchQuery={markdownLinkState.searchQuery}
        selectedIndex={markdownLinkState.selectedIndex}
        onSelect={handleMarkdownLinkSelect}
        onCreateNew={handleMarkdownLinkCreateNew}
        onClose={handleCloseMarkdownLinkSelector}
        onSearchChange={handleMarkdownLinkSearchChange}
        position={markdownLinkState.position}
        loading={markdownLinkState.loading}
      />

      {/* Image Lightbox */}
      {lightboxState.isVisible && (
        <ImageLightbox
          imageUrl={lightboxState.imageUrl}
          onClose={handleCloseLightbox}
          metadata={lightboxState.metadata}
        />
      )}

      {/* Translation Dialog */}
      <TranslationDialog
        isOpen={translationDialogState.isVisible}
        sourceText={translationDialogState.sourceText}
        onTranslate={handleTranslationExecute}
        onClose={handleTranslationDialogClose}
        isTranslating={translationDialogState.isTranslating}
        error={translationDialogState.error}
      />
    </div>
  );
};

export default BlockEditor;
